import {
  DataObjectContextProvider,
  DataObjectForm,
  DataObjectType,
  DataObjectView,
  PermissionCode,
  Spinner,
  useCheckForLockingError,
  useGetDataObjectVersionEditQuery,
  useHasPermissionForVersion,
  useNavigationBlockers,
  useRemoveDataObjectVersionEditQueries,
  useAlertContext,
} from '@atlas-frontend/sdk';
import { FC, useCallback, useEffect } from 'react';
import { useNumericParam } from '../../common/useEntityIdParam';
import { Forbidden } from '../Forbidden';
import { NotFound } from '../NotFound';
import {
  DataObjectBreadcrumbs,
  DataObjectBreadcrumbsData,
} from './DataObjectBreadcrumbs';
import { RouteObject } from '../../common/types';
import { useOpenTestCalculationReport } from './useOpenTestCalculation';
import { useOpenTestValidationReport } from './useOpenTestValidation';

export const DataObjectEdit: FC<{
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  view: DataObjectView;
  onCancel: (params?: { id: number; mode?: string }) => void;
}> = (props) => {
  const versionId = useNumericParam('id');
  const hasPermissionToManage = useHasPermissionForVersion();

  if (!versionId) {
    return <NotFound />;
  }

  return (
    <Forbidden
      isAllowed={hasPermissionToManage(
        props.view.dataObjectType === DataObjectType.IN
          ? PermissionCode.INPUT_DESC_MANAGE
          : PermissionCode.OUTPUT_DESC_MANAGE,
        versionId,
      )}
    >
      <DataObjectContextProvider>
        <DataObjectEditInner {...props} versionId={versionId} />
      </DataObjectContextProvider>
    </Forbidden>
  );
};

export const DataObjectEditInner: FC<{
  versionId: number;
  routes: Array<RouteObject<DataObjectBreadcrumbsData>>;
  view: DataObjectView;
  onCancel: () => void;
}> = (props) => {
  const { versionId, routes, view, onCancel } = props;
  const { resetAlert, setSuccess } = useAlertContext();
  const { clear: clearBlockers } = useNavigationBlockers();
  const { dataObjectGroupType } = view;
  const checkForLockingError = useCheckForLockingError(
    dataObjectGroupType,
    onCancel,
  );
  const openTestCalculationReport = useOpenTestCalculationReport();
  const openTestValidationReport = useOpenTestValidationReport();

  const {
    data: dataObjectVersion,
    refetch,
    error,
    isLoading,
  } = useGetDataObjectVersionEditQuery(
    {
      id: versionId,
      lock: true,
    },
    {
      refetchOnMount: 'always',
    },
  );
  const removeQueryData = useRemoveDataObjectVersionEditQueries();

  useEffect(() => {
    if (error) {
      checkForLockingError(error);
    }
  }, [error, checkForLockingError]);

  const handleSuccess = useCallback(async () => {
    await refetch();
    clearBlockers();
    setSuccess();
    removeQueryData();
  }, [removeQueryData, setSuccess, refetch, clearBlockers]);

  if (!dataObjectVersion && isLoading) {
    return <Spinner />;
  }

  if (!dataObjectVersion) {
    return null;
  }

  return (
    <DataObjectForm
      dataObjectView={view}
      mode="edit"
      id={versionId}
      dataObjectDescriptionVersion={dataObjectVersion}
      preSubmit={resetAlert}
      onSuccess={handleSuccess}
      onClickCancel={onCancel}
      breadCrumb={
        <DataObjectBreadcrumbs data={{ dataObjectVersion }} routes={routes} />
      }
      openTestCalculate={openTestCalculationReport}
      openTestValidate={openTestValidationReport}
    />
  );
};
